/**
 * Augmented Live agent-asset planning — pure logic (ENG-6767 Phase 1 / ENG-6778).
 * Design: docs/design/here-now-s3-realtime.md
 *
 * Agents persist media (images first; video/audio later) to the artifacts
 * bucket and embed a stable CDN URL in `source_html`, instead of inlining
 * `data:` URIs that blow the 1 MB source_html cap.
 *
 * Object layout (immutable, content-addressed):
 *   {slug}/assets/{sha256}.{ext}   Cache-Control: public, max-age=31536000, immutable
 *
 * Content-addressing by SHA-256 makes uploads idempotent (re-uploading the same
 * bytes maps to the same key) and dedups across re-publishes. Keyed under the
 * {slug}/ prefix so an artefact's assets share its lifecycle/ownership boundary
 * (a future delete is a single prefix sweep), exactly like {slug}/content/.
 *
 * SECURITY (council ENG-6767): image/svg+xml is intentionally NOT allowed.
 * Assets are served as top-level, same-origin URLs on the brand CDN domain —
 * the same origin whose viewer shell carries the realtime anon key — and the
 * content iframe's sandbox does NOT extend to a directly-opened asset URL, so an
 * SVG <script> would execute unsandboxed (stored XSS). Only inert binary media
 * are accepted — rasters, mp3 audio, and mp4/webm video (all inert containers);
 * image/svg+xml is deliberately excluded. Publish-time magic-byte sniffing
 * (sniffMediaType) enforces that an embedded asset's real type matches an enabled
 * one, so a Content-Type-spoofed polyglot can't be served as active content.
 */

/** Immutable, content-addressed asset objects can be cached forever. */
export const ASSET_CACHE_CONTROL = 'public, max-age=31536000, immutable';

export interface AssetTypeSpec {
  /** Canonical file extension for the S3 key (no dot). */
  ext: string;
  /** Per-asset byte cap for this content-type. */
  maxBytes: number;
  /** Whether agents may upload this type today. */
  enabled: boolean;
}

const MB = 1024 * 1024;

/**
 * Content-type allowlist. Images, audio (mp3), and video (mp4/webm) are enabled;
 * image/svg+xml is deliberately absent — see the module header.
 */
export const ASSET_TYPES: Readonly<Record<string, AssetTypeSpec>> = {
  'image/png': { ext: 'png', maxBytes: 5 * MB, enabled: true },
  'image/jpeg': { ext: 'jpg', maxBytes: 5 * MB, enabled: true },
  'image/webp': { ext: 'webp', maxBytes: 5 * MB, enabled: true },
  'image/gif': { ext: 'gif', maxBytes: 10 * MB, enabled: true },
  // Video (guided-tour narration clips). Inert ISO-BMFF / EBML containers — like
  // the rasters and mp3 above, not active content — so serving them as top-level
  // same-origin asset URLs is safe (the SVG/HTML-polyglot risk in the module
  // header doesn't apply; publish-time magic-byte sniffing enforces it). Uploads
  // are presigned-PUT straight to S3, and S3+CloudFront serve range requests so
  // <video> seeking works.
  'video/mp4': { ext: 'mp4', maxBytes: 100 * MB, enabled: true },
  'video/webm': { ext: 'webm', maxBytes: 100 * MB, enabled: true },
  // ENG-7048: enabled for server-generated ElevenLabs voiceover (text-to-speech).
  // MP3 is an inert binary container (like the rasters above), so serving it as a
  // top-level same-origin asset URL is safe — the SVG/HTML-polyglot risk in the
  // module header does not apply. Embedded as <audio src=...> in the page HTML.
  'audio/mpeg': { ext: 'mp3', maxBytes: 20 * MB, enabled: true },
};

const SHA256_HEX_RE = /^[0-9a-f]{64}$/;

export type AssetValidationError = {
  ok: false;
  code: 'unsupported_type' | 'too_large' | 'bad_checksum';
  message: string;
};

export interface AssetValidationOk {
  ok: true;
  /** Canonical extension for the key. */
  ext: string;
  /** Normalized (trimmed, lowercased) content-type to pin in the presigned PUT. */
  contentType: string;
}

/**
 * Validate an upload request against the allowlist + per-type size cap + the
 * checksum shape. Returns the canonical extension on success, or an
 * agent-actionable error (it names the allowed types / the cap) on failure.
 *
 * Note: this validates the *claim* (declared content-type + size + hash). With
 * presigned-PUT the server never sees the bytes at upload time, so magic-byte
 * verification happens at publish (Phase 2). image/svg+xml never reaches here.
 */
export function validateAsset(input: {
  contentType: string;
  byteSize: number;
  sha256: string;
}): AssetValidationOk | AssetValidationError {
  const contentType = (input.contentType ?? '').trim().toLowerCase();
  const spec = ASSET_TYPES[contentType];
  if (!spec || !spec.enabled) {
    const allowed = Object.entries(ASSET_TYPES)
      .filter(([, s]) => s.enabled)
      .map(([t]) => t)
      .join(', ');
    return {
      ok: false,
      code: 'unsupported_type',
      message: `Unsupported content_type "${input.contentType}". Allowed: ${allowed}.`,
    };
  }
  if (!SHA256_HEX_RE.test(input.sha256 ?? '')) {
    return {
      ok: false,
      code: 'bad_checksum',
      message: 'content_sha256 must be a lowercase hex SHA-256 (64 chars) of the asset bytes.',
    };
  }
  if (!Number.isInteger(input.byteSize) || input.byteSize <= 0) {
    return { ok: false, code: 'too_large', message: 'byte_size must be a positive integer number of bytes.' };
  }
  if (input.byteSize > spec.maxBytes) {
    return {
      ok: false,
      code: 'too_large',
      message: `Asset is ${input.byteSize} bytes; the limit for ${contentType} is ${spec.maxBytes} bytes.`,
    };
  }
  return { ok: true, ext: spec.ext, contentType };
}

/** S3 key for a content-addressed asset under its artefact's slug prefix. */
export function assetObjectKey(slug: string, sha256: string, ext: string): string {
  return `${slug}/assets/${sha256}.${ext}`;
}

/** Absolute CDN URL the agent embeds in source_html. */
export function buildAssetUrl(cdnDomain: string, slug: string, sha256: string, ext: string): string {
  return `https://${stripScheme(cdnDomain)}/${assetObjectKey(slug, sha256, ext)}`;
}

/** Drop a leading scheme so a configured `https://host` or a bare `host` both work. */
function stripScheme(domain: string): string {
  return domain.replace(/^https?:\/\//, '');
}

// ─── Publish-time verification (ENG-6780 Phase 2a) ──────────────────────────
// The upload is presigned, so the server never sees the bytes at upload time.
// At publish we re-derive the asset's real type from its magic bytes and reject
// anything that isn't a genuine enabled image — this is what stops a file
// uploaded with a lying `Content-Type: image/png` (an HTML/SVG polyglot) from
// being served as active content from the same-origin brand CDN.

/**
 * Sniff an image type from a buffer's leading magic bytes. Returns the canonical
 * MIME type for PNG/JPEG/GIF/WebP, or null for anything else (incl. SVG/HTML/text,
 * which have no binary signature). Only needs the first ~12 bytes.
 */
export function sniffImageType(bytes: Uint8Array): string | null {
  const b = bytes;
  // PNG: 89 50 4E 47 0D 0A 1A 0A
  if (
    b.length >= 8 &&
    b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47 &&
    b[4] === 0x0d && b[5] === 0x0a && b[6] === 0x1a && b[7] === 0x0a
  ) {
    return 'image/png';
  }
  // JPEG: FF D8 FF
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg';
  // GIF: "GIF87a" / "GIF89a"
  if (
    b.length >= 6 &&
    b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x38 &&
    (b[4] === 0x37 || b[4] === 0x39) && b[5] === 0x61
  ) {
    return 'image/gif';
  }
  // WebP: "RIFF"...."WEBP"
  if (
    b.length >= 12 &&
    b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 &&
    b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50
  ) {
    return 'image/webp';
  }
  return null;
}

/**
 * Sniff an MP3 (audio/mpeg) from a buffer's leading magic bytes (ENG-7048).
 *
 * An MP3 is either a bare MPEG-audio frame or an ID3v2 tag followed by one.
 * ElevenLabs' `mp3_44100_128` output begins with a raw frame sync (`FF FB`). We
 * validate an MP3-SPECIFIC frame header rather than just the 0xFF sync bits,
 * because a bare-sync check also accepts ADTS AAC (`FF F1` / `FF F9`) and other
 * MPEG-family streams — which would let a non-MP3 blob declared `audio/mpeg`
 * slip through the publish-time gate (CR, ENG-7048). The frame header's second
 * byte encodes sync(3) + version(2) + LAYER(2) + protection(1); MP3 is Layer III
 * (layer bits `01`), so we require that and reject the reserved version.
 *
 * When an ID3v2 tag leads, we skip it (syncsafe size) to reach the first frame
 * and validate THAT, so a tagged AAC file can't pass on the tag alone.
 */
export function sniffAudioType(bytes: Uint8Array): string | null {
  const b = bytes;
  let offset = 0;
  // Skip a leading ID3v2 tag to reach the first audio frame. Header is 10 bytes;
  // bytes 6-9 are a syncsafe (7-bit) tag-body size.
  if (b.length >= 10 && b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33) {
    const size =
      ((b[6]! & 0x7f) << 21) | ((b[7]! & 0x7f) << 14) | ((b[8]! & 0x7f) << 7) | (b[9]! & 0x7f);
    offset = 10 + size;
  }
  if (b.length < offset + 2) return null;
  const h0 = b[offset]!;
  const h1 = b[offset + 1]!;
  // MPEG-audio frame sync (11 set bits) + Layer III (layer bits == 01) +
  // non-reserved MPEG version (version bits != 01). This accepts MP3 (incl.
  // ElevenLabs' `FF FB`) and rejects ADTS AAC (layer bits 00).
  if (h0 === 0xff && (h1 & 0xe0) === 0xe0 && (h1 & 0x06) === 0x02 && (h1 & 0x18) !== 0x08) {
    return 'audio/mpeg';
  }
  return null;
}

/** Recognised ISO-BMFF major brands for MP4 *video* (excludes audio-only like "M4A "). */
const MP4_VIDEO_BRANDS = new Set([
  'isom', 'iso2', 'iso4', 'iso5', 'iso6', 'mp41', 'mp42', 'avc1', 'dash', 'mmp4', 'm4v ', 'f4v ', 'cmfc',
]);

/**
 * Sniff a video container (video/mp4 or video/webm) from leading magic bytes.
 *
 * MP4 is ISO Base Media File Format: bytes 4..7 are the box type "ftyp" and bytes
 * 8..11 are the major brand. We require a recognised ISO-BMFF *video* brand
 * (isom/iso2/mp41/mp42/avc1/dash/…) so a bare "ftyp" — e.g. an .m4a audio
 * container ("M4A ") or a crafted prefix — can't pass as video/mp4. WebM is an
 * EBML stream starting with 1A 45 DF A3. Only the first ~12 bytes are needed;
 * HTML/SVG have no such signature, so the stored-XSS guard holds.
 */
export function sniffVideoType(bytes: Uint8Array): string | null {
  const b = bytes;
  // ISO-BMFF (MP4): "ftyp" box type at offset 4, recognised video brand at offset 8.
  if (b.length >= 12 && b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) {
    const brand = String.fromCharCode(b[8]!, b[9]!, b[10]!, b[11]!).toLowerCase();
    if (
      MP4_VIDEO_BRANDS.has(brand) ||
      brand.startsWith('iso') ||
      brand.startsWith('mp4') ||
      brand.startsWith('avc')
    ) {
      return 'video/mp4';
    }
  }
  // WebM (EBML): 1A 45 DF A3
  if (b.length >= 4 && b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) {
    return 'video/webm';
  }
  return null;
}

/**
 * Sniff any ENABLED media asset type (image, audio, or video) from leading magic
 * bytes. This is the publish-time gate: an embedded asset URL must resolve to a
 * genuine enabled binary type. Inert formats only — SVG/HTML have no signature
 * and so never match (the stored-XSS guard in the module header). Returns the
 * canonical MIME or null.
 */
export function sniffMediaType(bytes: Uint8Array): string | null {
  return sniffImageType(bytes) ?? sniffAudioType(bytes) ?? sniffVideoType(bytes);
}

export interface AssetRef {
  sha256: string;
  ext: string;
  url: string;
}

/**
 * Find references to THIS artefact's own assets in published HTML — i.e. URLs of
 * the form `https://{cdnDomain}/{slug}/assets/{sha256}.{ext}`. Only our-origin,
 * this-slug assets are governed (a public third-party image URL is just a normal
 * embed). Deduped by sha256+ext.
 */
export function extractAssetRefs(html: string, cdnDomain: string, slug: string): AssetRef[] {
  const host = stripScheme(cdnDomain);
  const tail = `${escapeRegExp(slug)}/assets/([0-9a-f]{64})\\.([a-z0-9]+)`;
  // The viewer serves the page from our CDN origin, so an agent can reference an
  // asset three ways — all of which must be verified:
  //   (a) absolute or protocol-relative on OUR host: https://host/.. or //host/..
  //   (b) root-relative (same-origin): /{slug}/assets/..
  // The root-relative form is anchored to a leading delimiter (start / quote /
  // whitespace / "(" / "=") so a FOREIGN absolute URL (https://evil/{slug}/..)
  // can't false-match the bare-path branch.
  const patterns = [
    new RegExp(`(?:https?:)?//${escapeRegExp(host)}/${tail}`, 'g'),
    new RegExp(`(?:^|[\\s"'(=])/${tail}`, 'g'),
  ];
  const out: AssetRef[] = [];
  const seen = new Set<string>();
  for (const re of patterns) {
    let m: RegExpExecArray | null;
    while ((m = re.exec(html)) !== null) {
      const sha256 = m[1]!;
      const ext = m[2]!;
      const dedup = `${sha256}.${ext}`;
      if (seen.has(dedup)) continue;
      seen.add(dedup);
      out.push({ sha256, ext, url: m[0] });
    }
  }
  return out;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ─── Server-side de-inlining (ENG-6781 Phase 3) ─────────────────────────────
// Externalize inline base64 `data:` image URIs to S3 at publish so the agent can
// keep inlining (no new tool to learn) while the served HTML stays small —
// fixing the 1 MB source_html cap for every agent automatically.

export interface DataUriImage {
  /** The full `data:...;base64,...` substring, used for in-place replacement. */
  full: string;
  /** Declared MIME (lowercased). The real type is re-derived from the bytes. */
  mime: string;
  /** The base64 payload. */
  b64: string;
}

/**
 * Find base64 `data:image/*` URIs in HTML. Only base64 image data URIs are
 * returned — these are the payloads that bloat source_html toward the cap.
 * Pure parsing; the caller decodes, verifies magic bytes, stores, and rewrites.
 */
export function findDataUriImages(html: string): DataUriImage[] {
  // Case-insensitive: data: URIs allow `IMAGE/PNG` and `;BASE64,`. The captured
  // mime is lowercased by the caller; the real type is re-derived from the bytes.
  const re = /data:(image\/[a-z0-9.+-]+);base64,([A-Za-z0-9+/=]+)/gi;
  const out: DataUriImage[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    out.push({ full: m[0], mime: m[1]!.toLowerCase(), b64: m[2]! });
  }
  return out;
}
